import './applyConfirmVehiclePage.scss';

import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import { ThemeProvider } from '@mui/material/styles';
import TextField from '@mui/material/TextField';
import { ApplicationType } from '@thesavingsgroup/enums/ApplicationType';
import { isNonEmptyString } from '@thesavingsgroup/utils/isNonEmptyString';
import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import toString from 'lodash/toString';
import { ReactNode, useEffect, useMemo, useState } from 'react';
import { FieldValues, useForm } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';

import VehicleImagePlaceholder from '../../../assets/vehicle-placeholder.png';
import { Button } from '../../../components/Button/Button';
import { Header } from '../../../components/Header/Header';
import { NavigationPane } from '../../../components/NavigationPane/NavigationPane';
import { Select } from '../../../components/Select/Select';
import { inputEventHandler } from '../../../components/utils/inputEventHandler';
import { resetStore as defaultResetStore } from '../../../store/_core/resetStore';
import useApiStore from '../../../store/apiStore/useApiStore';
import useApplicantStore from '../../../store/applicantStore/useApplicantStore';
import useApplyStore from '../../../store/applyStore/useApplyStore';
import useCoapplicantStore from '../../../store/coapplicantStore/useCoapplicantStore';
import useDashboardStore from '../../../store/dashboardStore/useDashboardStore';
import { useNavigateWithCallback } from '../../../store/routeStore/hooks/useNavigateWithCallback';
import useThemeStore from '../../../store/themeStore/useThemeStore';
import useWorkflowStore from '../../../store/workflowStore/useWorkflowStore';
import { locator } from '../../../testing/e2e-locator/locator';
import { ApplyConfirmVehiclePageProps } from './types/ApplyConfirmVehiclePageProps';

export function Component({
  resetStore = defaultResetStore,
}: ApplyConfirmVehiclePageProps): ReactNode {
  const { createTheme } = useThemeStore();
  const theme = createTheme();
  const navigate = useNavigate();
  const navigateWithCallback = useNavigateWithCallback();

  const { isLoading } = useApiStore();

  const {
    applicationType,
    computed: { yearMakeModel },
    fetchVehicleImageByTrim,
    fetchVehicleImageByTrimFailed,
    fetchVehicleTrims,
    fetchVehicleTrimsFailedMessage,
    isDuplicateVehicleError,
    mileage,
    payoffAmount,
    purchasePrice,
    resetSubmitValues,
    setMileage,
    setPayoffAmount,
    setPurchasePrice,
    setTrimByValue,
    submit,
    submitCompleted,
    submitFailedMessage,
    trim,
    trims,
    vehicleImage,
  } = useApplyStore();

  const { t } = useTranslation();
  const { previous } = useWorkflowStore();

  const {
    control,
    register,
    handleSubmit,
    setValue,
    trigger,
    formState: { errors, isValid },
  } = useForm({
    defaultValues: {
      mileageValue: inputEventHandler.number.formatter(mileage),
      payoffAmountValue: inputEventHandler.currency.formatter(payoffAmount),
      purchasePriceValue: inputEventHandler.currency.formatter(purchasePrice),
      trimValue: get(trim, 'value', '') as string,
    },
    mode: 'onBlur',
    criteriaMode: 'firstError',
    delayError: 200,
  });

  const trimValidation = {
    required: t('validation.trim.required'),
  };

  const mileageValidation = {
    setValueAs: (value: string): string =>
      inputEventHandler.number.parser(value),
    required: t('validation.mileage.required'),
  };

  const payoffAmountValidation = {
    setValueAs: (value: string): string =>
      inputEventHandler.currency.parser(value),
    required: t('validation.payoffAmount.required'),
    min: {
      value: 1,
      message: t('validation.payoffAmount.min', { min: '$0' }),
    },
    max: {
      value: 999999,
      message: t('validation.payoffAmount.max', { max: '$1,000,000' }),
    },
  };

  const purchasePriceValidation = {
    setValueAs: (value: string): string =>
      inputEventHandler.currency.parser(value),
    required: t('validation.purchasePrice.required'),
    min: {
      value: 1,
      message: t('validation.purchasePrice.min', { min: '$0' }),
    },
    max: {
      value: 999999,
      message: t('validation.purchasePrice.max', { max: '$1,000,000' }),
    },
  };

  const [imageLoading, setImageLoading] = useState(false);

  const displayVehicleImage = useMemo((): boolean => {
    return vehicleImage && !imageLoading;
  }, [vehicleImage, imageLoading]);

  const displayPlaceholderImage = useMemo((): boolean => {
    return isEmpty(vehicleImage) || imageLoading;
  }, [vehicleImage, imageLoading]);

  const displayPayoffAmount = useMemo((): boolean => {
    return applicationType === ApplicationType.REFINANCE;
  }, [applicationType]);

  const displayPurchasePrice = useMemo((): boolean => {
    return (
      applicationType === ApplicationType.PURCHASE ||
      applicationType === ApplicationType.LEASE_BUYOUT
    );
  }, [applicationType]);

  const nextStep = (): void => {
    const {
      computed: { vehicleId },
    } = useDashboardStore.getState();

    // Reset applicant and coapplicant stores to ensure a clean state for the applicant workflow.
    resetStore(useApplicantStore);
    resetStore(useCoapplicantStore);

    navigateWithCallback(`/applicant/${vehicleId}`).then((): void => {
      // Reset the apply store to ensure a clean state for the next application.
      resetStore(useApplyStore);
    });
  };

  const previousStep = (): void => {
    navigate(previous());
  };

  const reloadVehicleImage = (value: string): void => {
    const selectedTrim = trims.find(
      (trimObj): boolean => trimObj.value === value,
    );

    if (selectedTrim) {
      resetSubmitValues();

      setImageLoading(true);
      fetchVehicleImageByTrim(selectedTrim);
    }
  };

  const formSubmit = (values: FieldValues): Promise<void> => {
    const { trimValue, mileageValue, payoffAmountValue, purchasePriceValue } =
      values;

    if (
      !trimValue ||
      !mileageValue ||
      (displayPayoffAmount && !payoffAmountValue) ||
      (displayPurchasePrice && !purchasePriceValue)
    ) {
      return;
    }

    setTrimByValue(trimValue);
    setMileage(mileageValue);

    if (displayPayoffAmount) {
      setPayoffAmount(payoffAmountValue);
    }

    if (displayPurchasePrice) {
      setPurchasePrice(purchasePriceValue);
    }

    submit();
  };

  useEffect((): void => {
    resetSubmitValues();

    fetchVehicleTrims();

    if (!trim) {
      // when trim is not available, reset the vehicle image
      useApplyStore.setState({ vehicleImage: null });

      return;
    }

    setImageLoading(true);

    fetchVehicleImageByTrim(trim);
  }, []);

  useEffect((): void => {
    // image loading is tracked by the onLoad event of the image tag.
    // This is done to prevent the loading animation from ending upon the completion of the request alone,
    // whereas the animation should end when the image is fully loaded.

    // if the request for the image url failed, end the loading animation
    if (fetchVehicleImageByTrimFailed) {
      setImageLoading(false);
    }
  }, [fetchVehicleImageByTrimFailed]);

  useEffect((): void => {
    if (submitCompleted && !submitFailedMessage) {
      nextStep();
    }
  }, [submitCompleted, submitFailedMessage]);

  return (
    <div className="apply-confirm-vehicle-page">
      <ThemeProvider theme={theme}>
        <Box sx={theme.pageLayout}>
          <Header
            progress={useWorkflowStore.getState().computed.progress}
            showDrawerToggle
            onBackClick={previousStep}
          />

          <main data-testid={locator.pages.applyConfirmVehicle.main.root}>
            <figure>
              <div>
                <img
                  alt={t('imageAlts.selectedVehicle')}
                  className={displayVehicleImage ? 'show' : 'hide'}
                  data-testid={
                    locator.pages.applyConfirmVehicle.main.selectedVehicleImage
                  }
                  src={vehicleImage || undefined}
                  onLoad={(): void => {
                    setImageLoading(false);
                  }}
                />

                <img
                  alt={t('imageAlts.selectedVehicle')}
                  className={displayPlaceholderImage ? 'show' : 'hide'}
                  data-testid={
                    locator.pages.applyConfirmVehicle.main
                      .placeholderVehicleImage
                  }
                  src={VehicleImagePlaceholder}
                />

                {imageLoading && <span className="skeleton-loading-sweep" />}
              </div>
            </figure>
            <form
              data-testid={locator.pages.applyConfirmVehicle.form.root}
              noValidate
              onSubmit={handleSubmit(formSubmit)}
            >
              <div className="form-content">
                <h1 data-testid={locator.pages.applyConfirmVehicle.form.h1}>
                  {yearMakeModel}
                </h1>
                <h2 data-testid={locator.pages.applyConfirmVehicle.form.h2}>
                  {t('ApplyConfirmVehiclePage.subtitle')}
                </h2>

                {!isEmpty(fetchVehicleTrimsFailedMessage) && (
                  <div style={{ marginBottom: '2rem' }}>
                    <Alert
                      data-testid={
                        locator.pages.applyConfirmVehicle.alert
                          .vehicleTrimsError
                      }
                      icon={false}
                      severity="error"
                    >
                      {fetchVehicleTrimsFailedMessage}
                    </Alert>
                  </div>
                )}

                {isNonEmptyString(submitFailedMessage) && (
                  <div style={{ marginBottom: '2rem' }}>
                    <Alert
                      data-testid={
                        locator.pages.applyConfirmVehicle.alert
                          .submitFailedError
                      }
                      icon={false}
                      severity="error"
                    >
                      <Trans
                        components={{
                          dashboard: <Link to="/dashboard/garage" />,
                        }}
                      >
                        {submitFailedMessage}
                      </Trans>
                    </Alert>
                  </div>
                )}

                <div style={{ marginBottom: '3rem' }}>
                  <div>
                    <Select
                      control={control}
                      errorTestId={
                        locator.pages.applyConfirmVehicle.form.trimSelectError
                      }
                      inputTestId={
                        locator.pages.applyConfirmVehicle.form.trimSelect
                      }
                      label={t('ApplyConfirmVehiclePage.trim')}
                      name="trimValue"
                      options={trims.map((trimObj) => ({
                        name: trimObj.label,
                        value: trimObj.value,
                      }))}
                      rules={trimValidation}
                      onChange={reloadVehicleImage}
                    />
                  </div>

                  <div style={{ marginTop: '1.5rem' }}>
                    <TextField
                      {...register('mileageValue', mileageValidation)}
                      error={!isEmpty(errors.mileageValue)}
                      label={t('ApplyConfirmVehiclePage.mileage')}
                      slotProps={{
                        htmlInput: {
                          'data-testid':
                            locator.pages.applyConfirmVehicle.form.mileageInput,
                          inputMode: 'numeric',
                          min: 0,
                        },
                      }}
                      type="text"
                      variant="outlined"
                      fullWidth
                      onChange={inputEventHandler.number.change(
                        'mileageValue',
                        setValue,
                      )}
                      onKeyDown={inputEventHandler.number.keydown}
                      onPasteCapture={inputEventHandler.number.pasteCapture}
                    />

                    {!isEmpty(errors.mileageValue) && (
                      <span
                        className="field-error"
                        data-testid={
                          locator.pages.applyConfirmVehicle.form
                            .mileageInputError
                        }
                      >
                        {toString(errors.mileageValue.message)}
                      </span>
                    )}
                  </div>

                  {displayPayoffAmount && (
                    <div style={{ marginTop: '1.5rem' }}>
                      <TextField
                        {...register(
                          'payoffAmountValue',
                          payoffAmountValidation,
                        )}
                        error={!isEmpty(errors.payoffAmountValue)}
                        label={t('ApplyConfirmVehiclePage.payoffAmount')}
                        slotProps={{
                          htmlInput: {
                            'data-testid':
                              locator.pages.applyConfirmVehicle.form
                                .payoffAmountInput,
                            inputMode: 'numeric',
                          },
                        }}
                        type="text"
                        variant="outlined"
                        fullWidth
                        onBlur={inputEventHandler.currency.blur(
                          'payoffAmountValue',
                          setValue,
                          trigger,
                        )}
                        onChange={inputEventHandler.currency.change(
                          'payoffAmountValue',
                          setValue,
                        )}
                        onFocus={inputEventHandler.currency.focus(
                          'payoffAmountValue',
                          setValue,
                        )}
                        onKeyDown={inputEventHandler.number.keydown}
                        onPasteCapture={inputEventHandler.number.pasteCapture}
                      />

                      {!isEmpty(errors.payoffAmountValue) && (
                        <span
                          className="field-error"
                          data-testid={
                            locator.pages.applyConfirmVehicle.form
                              .payoffAmountInputError
                          }
                        >
                          {toString(errors.payoffAmountValue.message)}
                        </span>
                      )}
                    </div>
                  )}

                  {displayPurchasePrice && (
                    <div style={{ marginTop: '1.5rem' }}>
                      <TextField
                        {...register(
                          'purchasePriceValue',
                          purchasePriceValidation,
                        )}
                        error={!isEmpty(errors.purchasePriceValue)}
                        label={t('ApplyConfirmVehiclePage.purchasePrice')}
                        slotProps={{
                          htmlInput: {
                            'data-testid':
                              locator.pages.applyConfirmVehicle.form
                                .purchasePriceInput,
                            inputMode: 'numeric',
                          },
                        }}
                        type="text"
                        variant="outlined"
                        fullWidth
                        onBlur={inputEventHandler.currency.blur(
                          'purchasePriceValue',
                          setValue,
                          trigger,
                        )}
                        onChange={inputEventHandler.currency.change(
                          'purchasePriceValue',
                          setValue,
                        )}
                        onFocus={inputEventHandler.currency.focus(
                          'purchasePriceValue',
                          setValue,
                        )}
                        onKeyDown={inputEventHandler.number.keydown}
                        onPasteCapture={inputEventHandler.number.pasteCapture}
                      />

                      {!isEmpty(errors.purchasePriceValue) && (
                        <span
                          className="field-error"
                          data-testid={
                            locator.pages.applyConfirmVehicle.form
                              .purchasePriceInputError
                          }
                        >
                          {toString(errors.purchasePriceValue.message)}
                        </span>
                      )}
                    </div>
                  )}
                </div>

                <Button
                  data-testid={
                    locator.pages.applyConfirmVehicle.form.submitButton
                  }
                  disabled={
                    (isLoading && !imageLoading) ||
                    isDuplicateVehicleError ||
                    !isValid
                  }
                  loader={isLoading && !imageLoading}
                  loaderPosition="start"
                  fullWidth
                  submit
                >
                  {t('ApplyConfirmVehiclePage.continue')}
                </Button>
              </div>
              <footer
                data-testid={locator.pages.applyConfirmVehicle.footer.root}
              >
                {t('copyright', { year: new Date().getFullYear() })}
              </footer>
            </form>
          </main>
        </Box>

        <NavigationPane withDrawer withUserNameHeader />
      </ThemeProvider>
    </div>
  );
}

Component.displayName = 'ApplyConfirmVehiclePage';
